import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { auth, db } from '../lib/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { supabase } from '../lib/supabase';
import { TopNav } from '../components/TopNav';
import { ChevronLeft, Save, Activity, DollarSign, Wallet, ShieldAlert, Edit2, Utensils, Armchair, History, Calendar } from 'lucide-react';
import clsx from 'clsx';

export const PlayerPage = () => {
    const { playerId } = useParams();
    const navigate = useNavigate();
    const { t, language } = useLanguage();

    const [playerInfo, setPlayerInfo] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState('');
    const [mealPref, setMealPref] = useState('');
    const [seatPref, setSeatPref] = useState('');
    const [sessionHistory, setSessionHistory] = useState([]);
    const [isSavingNotes, setIsSavingNotes] = useState(false);
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [isEditingPrefs, setIsEditingPrefs] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    const hostId = auth.currentUser?.uid;

    useEffect(() => {
        const fetchPlayerData = async () => {
            if (!hostId || !playerId) return;

            try {
                // 1. Fetch from Firestore for baseline info and phone number
                const fmDoc = await getDoc(doc(db, `hosts/${hostId}/players/${playerId}`));
                
                if (!fmDoc.exists()) {
                    console.error("Player not found in Firestore");
                    setLoading(false);
                    return;
                }
                
                const fData = fmDoc.data();
                setPlayerInfo({ id: fmDoc.id, ...fData });

                // 2. If we have a phone number, fetch from Supabase
                if (fData.phone) {
                    const { data: sData, error } = await supabase
                        .from('player_lifetime_stats')
                        .select('*')
                        .eq('host_id', hostId)
                        .eq('phone', fData.phone)
                        .single();

                    if (!error && sData) {
                        setStats(sData);
                        if (sData.administrative_notes) setNotes(sData.administrative_notes);
                        if (sData.meal_preference) setMealPref(sData.meal_preference);
                        if (sData.seat_preference) setSeatPref(sData.seat_preference);
                    } else {
                        console.log('No Supabase stats found', error);
                        // Player exists in Firestore but hasn't played tracking sessions in Supabase yet.
                        setStats({
                            total_sessions: 0,
                            total_buy_ins: 0,
                            total_cash_outs: 0,
                            net_profit: 0,
                            total_rake_paid: 0,
                            loyalty_score: 0
                        });
                    }

                    // 3. Fetch Session History
                    fetchSessionHistory(sData?.player_id || null);
                } else {
                    setStats({
                        total_sessions: 0,
                        total_buy_ins: 0,
                        total_cash_outs: 0,
                        net_profit: 0,
                        total_rake_paid: 0,
                        loyalty_score: 0
                    });
                }
            } catch (err) {
                console.error("Failed to load player profile:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchPlayerData();
    }, [hostId, playerId]);

    const fetchSessionHistory = async (sPlayerId) => {
        if (!sPlayerId) return;
        setIsLoadingHistory(true);
        try {
            const { data, error } = await supabase
                .from('session_registrations')
                .select(`
                    id,
                    buy_in_amount,
                    cash_out_amount,
                    rake_paid,
                    created_at,
                    sessions (
                        session_id,
                        game_name,
                        created_at
                    )
                `)
                .eq('player_id', sPlayerId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setSessionHistory(data || []);
        } catch (err) {
            console.error("Error fetching session history:", err);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const handleSavePrimaryData = async () => {
        if (!playerInfo?.phone) {
            alert('Cannot save. Player is missing a phone number.');
            return;
        }

        setIsSavingNotes(true);
        try {
            const { error } = await supabase
                .from('players')
                .update({ 
                    administrative_notes: notes,
                    meal_preference: mealPref,
                    seat_preference: seatPref
                })
                .eq('phone', playerInfo.phone);

            if (error) throw error;
            setIsEditingNotes(false);
            setIsEditingPrefs(false);
        } catch (error) {
            console.error("Error saving data:", error);
            alert("Failed to save changes.");
        } finally {
            setIsSavingNotes(false);
        }
    };

    const formatCurrency = (amount) => `₪${Number(amount || 0).toLocaleString()}`;

    if (loading) {
        return (
            <div className="flex flex-col min-h-[100dvh] bg-slate-950 text-white font-outfit" dir="rtl">
                <TopNav />
                <div className="flex-1 flex items-center justify-center">
                    <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    if (!playerInfo) {
        return (
            <div className="flex flex-col min-h-[100dvh] bg-slate-950 text-white font-outfit" dir="rtl">
                <TopNav />
                <div className="flex-1 p-6 flex flex-col items-center justify-center h-full">
                    <h2 className="text-xl font-bold font-heebo text-white mb-4">Player Not Found</h2>
                    <button 
                        onClick={() => navigate('/community')}
                        className="px-4 py-2 bg-amber-500 text-slate-950 rounded-lg font-heebo font-bold"
                    >
                        Back to Community
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-[100dvh] bg-slate-950 text-white font-outfit" dir="rtl">
            <TopNav />

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-slate-900/60 sticky top-0 z-10 w-full backdrop-blur-md">
                <button onClick={() => navigate('/community')} className="flex items-center gap-1.5 text-slate-400 hover:text-white text-sm font-heebo outline-none">
                    <ChevronLeft className="w-4 h-4 rotate-180" />
                    {t('navCommunity') || 'Community'}
                </button>
                <div className="flex items-center gap-2">
                    <span className="text-sm font-black uppercase tracking-wider text-amber-500 font-heebo">Profile</span>
                </div>
                <div className="w-16"></div> {/* Spacer for centering */}
            </div>

            <div className="flex-1 overflow-y-auto w-full">
                <div className="px-4 py-6 space-y-6 max-w-xl mx-auto w-full">

                    {/* Profile Header */}
                    <div className="flex items-start gap-4 p-5 bg-slate-900/40 rounded-3xl border border-white/5 shadow-2xl">
                        <div className="flex-shrink-0 w-16 h-16 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center text-2xl font-black text-amber-500 font-heebo shadow-inner">
                            {(playerInfo.name || '?')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h1 className="text-2xl font-black text-white truncate font-heebo mb-1 leading-none">{playerInfo.name}</h1>
                            <div className="flex items-center gap-2 text-sm text-slate-400 font-inter mb-3" dir="ltr">
                                {playerInfo.phone || 'No Phone'}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="px-2.5 py-1 bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] font-black rounded-lg uppercase tracking-wider font-inter inline-flex items-center gap-1">
                                    <ShieldAlert className="w-3 h-3" />
                                    Loyalty: {stats?.loyalty_score || 0}
                                </div>
                                <div className="px-2.5 py-1 bg-slate-800 border border-slate-700 text-slate-300 text-[10px] font-black rounded-lg uppercase tracking-wider font-inter inline-flex items-center gap-1">
                                    Credit: {formatCurrency(playerInfo.maxLimit || playerInfo.debtLimit || 0)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Financial HUD */}
                    <div className="grid grid-cols-2 gap-3 pb-2">
                        <div className="bg-slate-900/60 p-4 rounded-2xl border border-blue-500/15">
                            <div className="flex items-center gap-1.5 mb-2">
                                <Activity className="w-3.5 h-3.5 text-blue-400" />
                                <span className="text-xs font-black text-blue-300 uppercase tracking-widest font-inter">Sessions</span>
                            </div>
                            <div className="text-2xl font-black text-white tabular-nums">{stats?.total_sessions || 0}</div>
                        </div>
                        <div className="bg-slate-900/60 p-4 rounded-2xl border border-emerald-500/15">
                            <div className="flex items-center gap-1.5 mb-2">
                                <Wallet className="w-3.5 h-3.5 text-emerald-400" />
                                <span className="text-xs font-black text-emerald-300 uppercase tracking-widest font-inter">Net Profit</span>
                            </div>
                            <div className={clsx(
                                'text-2xl font-black tabular-nums',
                                (stats?.net_profit || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
                            )} dir="ltr">
                                {(stats?.net_profit || 0) >= 0 ? '+' : ''}{formatCurrency(stats?.net_profit)}
                            </div>
                        </div>
                        <div className="bg-slate-900/60 p-4 rounded-2xl border border-rose-500/15">
                            <div className="flex items-center gap-1.5 mb-2">
                                <DollarSign className="w-3.5 h-3.5 text-rose-400" />
                                <span className="text-xs font-black text-rose-300 uppercase tracking-widest font-inter">Total Buy In</span>
                            </div>
                            <div className="text-xl font-black text-white tabular-nums" dir="ltr">{formatCurrency(stats?.total_buy_ins)}</div>
                        </div>
                        <div className="bg-slate-900/60 p-4 rounded-2xl border border-cyan-500/15">
                            <div className="flex items-center gap-1.5 mb-2">
                                <DollarSign className="w-3.5 h-3.5 text-cyan-400" />
                                <span className="text-xs font-black text-cyan-300 uppercase tracking-widest font-inter">Total Rake</span>
                            </div>
                            <div className="text-xl font-black text-white tabular-nums" dir="ltr">{formatCurrency(stats?.total_rake_paid)}</div>
                        </div>
                    </div>

                    {/* Preferences Card */}
                    <div className="bg-slate-900/60 rounded-3xl border border-white/5 overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-slate-900/80">
                            <h3 className="text-sm font-black text-slate-200 font-heebo">Player Preferences</h3>
                            <button 
                                onClick={() => isEditingPrefs ? handleSavePrimaryData() : setIsEditingPrefs(true)}
                                disabled={isSavingNotes}
                                className={clsx(
                                    "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-black transition-all border font-heebo outline-none",
                                    isEditingPrefs 
                                        ? "bg-amber-500/20 border-amber-400 text-amber-400 hover:bg-amber-500/30"
                                        : "bg-slate-800 border-slate-700 text-slate-400 hover:text-white"
                                )}
                            >
                                {isEditingPrefs ? <Save className="w-3.5 h-3.5" /> : <Edit2 className="w-3.5 h-3.5" />}
                                {isEditingPrefs ? t('save') || 'Save' : t('edit') || 'Edit'}
                            </button>
                        </div>
                        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                    <Utensils className="w-3 h-3" /> Meal Preference
                                </label>
                                {isEditingPrefs ? (
                                    <input 
                                        type="text" 
                                        value={mealPref} 
                                        onChange={(e) => setMealPref(e.target.value)}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-amber-500/50 font-heebo"
                                        placeholder="e.g. Vegetarian, No Dairy..."
                                    />
                                ) : (
                                    <div className="text-sm text-slate-300 font-heebo bg-slate-950/40 p-2.5 rounded-xl border border-white/5 min-h-[40px] flex items-center">
                                        {mealPref || <span className="text-slate-600 italic">None specified</span>}
                                    </div>
                                )}
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                    <Armchair className="w-3 h-3" /> Seat Preference
                                </label>
                                {isEditingPrefs ? (
                                    <input 
                                        type="text" 
                                        value={seatPref} 
                                        onChange={(e) => setSeatPref(e.target.value)}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-amber-500/50 font-heebo"
                                        placeholder="e.g. Seat 1, Early seat..."
                                    />
                                ) : (
                                    <div className="text-sm text-slate-300 font-heebo bg-slate-950/40 p-2.5 rounded-xl border border-white/5 min-h-[40px] flex items-center">
                                        {seatPref || <span className="text-slate-600 italic">None specified</span>}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Administrative Notes */}
                    <div className="bg-slate-900/60 rounded-3xl border border-white/5 overflow-hidden">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-slate-900/80">
                            <h3 className="text-sm font-black text-slate-200 font-heebo">Administrative Notes</h3>
                            <button 
                                onClick={() => isEditingNotes ? handleSavePrimaryData() : setIsEditingNotes(true)}
                                disabled={isSavingNotes}
                                className={clsx(
                                    "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-black transition-all border font-heebo outline-none",
                                    isEditingNotes 
                                        ? "bg-amber-500/20 border-amber-400 text-amber-400 hover:bg-amber-500/30"
                                        : "bg-slate-800 border-slate-700 text-slate-400 hover:text-white"
                                )}
                            >
                                {isSavingNotes ? (
                                    <span className="w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                                ) : isEditingNotes ? (
                                    <>
                                        <Save className="w-3.5 h-3.5" /> Save Notes
                                    </>
                                ) : (
                                    <>
                                        <Edit2 className="w-3.5 h-3.5" /> Edit
                                    </>
                                )}
                            </button>
                        </div>
                        <div className="p-4">
                            {isEditingNotes ? (
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Add notes about player preferences, behavior, or limits..."
                                    className="w-full h-32 bg-slate-950 border border-slate-800 rounded-xl p-3 text-sm text-slate-300 focus:outline-none focus:border-amber-500/50 resize-none font-heebo placeholder:text-slate-600"
                                />
                            ) : (
                                <div className="text-sm text-slate-400 font-heebo whitespace-pre-wrap min-h-[4rem]">
                                    {notes ? notes : <span className="italic opacity-50">No administrative notes found.</span>}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Session History Container */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 px-1">
                            <History className="w-4 h-4 text-slate-500" />
                            <h3 className="text-sm font-black text-slate-300 uppercase tracking-widest">Session History</h3>
                        </div>

                        <div className="space-y-2">
                            {isLoadingHistory ? (
                                <div className="h-20 bg-slate-900/40 rounded-3xl animate-pulse" />
                            ) : sessionHistory.length === 0 ? (
                                <div className="bg-slate-900/40 border border-white/5 rounded-3xl p-8 flex flex-col items-center justify-center text-slate-600">
                                    <Activity className="w-8 h-8 mb-2 opacity-20" />
                                    <p className="text-xs font-heebo">No games played yet</p>
                                </div>
                            ) : sessionHistory.map((item) => {
                                const net = (Number(item.cash_out_amount) || 0) - (Number(item.buy_in_amount) || 0);
                                const date = new Date(item.created_at).toLocaleDateString(language === 'he' ? 'he-IL' : 'en-US', {
                                    day: 'numeric', month: 'short', year: 'numeric'
                                });
                                
                                return (
                                    <div key={item.id} className="bg-slate-900/60 border border-white/5 rounded-2xl overflow-hidden flex items-stretch">
                                        <div className={clsx(
                                            "w-1.5 flex-shrink-0",
                                            net > 0 ? "bg-emerald-500" : net < 0 ? "bg-rose-500" : "bg-slate-600"
                                        )} />
                                        <div className="flex-1 p-4 flex items-center justify-between bg-gradient-to-r from-slate-900/10 to-transparent">
                                            <div className="flex flex-col gap-0.5">
                                                <span className="text-xs font-black text-white font-heebo">{item.sessions?.game_name || 'Session'}</span>
                                                <div className="flex items-center gap-2 text-[10px] text-slate-500 font-inter">
                                                    <Calendar className="w-2.5 h-2.5" />
                                                    {date}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className={clsx(
                                                    "text-sm font-black tabular-nums",
                                                    net > 0 ? "text-emerald-400" : net < 0 ? "text-rose-400" : "text-slate-400"
                                                )}>
                                                    {net > 0 ? '+' : ''}{formatCurrency(net)}
                                                </div>
                                                <div className="text-[9px] text-slate-600 font-bold uppercase tracking-wider">
                                                    Rake: {formatCurrency(item.rake_paid)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};
